import * as React from 'react';
import { Database, FileText, Mic, Upload } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  DetailHead,
  ListGroupLabel,
  PartLabel,
  WorkspaceButton,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import {
  confirmInterviewProposal,
  confirmInterviewProposals,
  declineInterviewProposal,
  useIntakeQuery,
  useInterviewProposalsQuery,
  type IntakeFile,
  type InterviewProposal,
} from '@/lib/api/discovery-interview-proposals';
import { useDiscoveryWrite, type InterviewListItem } from '@/lib/api/discovery-sessions';
import { uploadFileChunked } from '@/lib/api/documents';
import { useEngagementQuery } from '@/lib/api/engagements';
import { cn } from '@/lib/utils/cn';
import { plural } from '../discovery-logic';
import { transcriptFile } from '../transcript-review/review-logic';
import { errorText } from './errors';
import { bulkOutcomeText } from './transcripts-to-place';

/**
 * Bring in files (design note section 3): one drop zone for transcripts and
 * client documents together. Each file is uploaded as a client document
 * (purpose `general`); the classifier and the two Discovery finders sort
 * them: a transcript gets a proposed placement with an interview (confirmed
 * here, a person decides every one), a client document is read for
 * evidence (reviewed in the queue), and anything else is skipped with its
 * reason. Names come first: "Goes to Lena Park, Grants manager (S12)".
 */

const ACCEPT = '.docx,.pdf,.txt,.vtt,.srt,.xlsx,.xls,.csv,.pptx,.doc';

const REASON: Record<string, string> = {
  contract: 'A contract or change order: Discovery does not read contracts.',
  vendor: 'A vendor document: Discovery reads client and Avero documents.',
  not_placed: 'Declined as an interview transcript: it stays a document.',
  nothing_found: 'Read: nothing to propose.',
  module_off: 'Discovery is off for this engagement.',
  discovery_not_started: 'Read once Discovery is set up.',
  party_not_read: 'A vendor document: Discovery reads client and Avero documents.',
  type_not_read: 'Not a kind of document Discovery reads.',
  already_read: 'Already read.',
  document_missing: 'The document was deleted.',
};
const reasonText = (f: IntakeFile) =>
  f.state === 'failed'
    ? `The reading failed${f.reason ? ` (${f.reason.replace(/_/g, ' ')})` : ''}. Read it again from Documents.`
    : (REASON[f.reason ?? ''] ?? (f.reason ? f.reason.replace(/_/g, ' ') : 'Not read.'));

interface Sending {
  name: string;
  pct: number;
}

export function BringInFiles({
  engagementId,
  interviews,
  onReview,
  onChange,
  onOpenInterview,
}: {
  engagementId: string;
  interviews: ReadonlyArray<InterviewListItem>;
  /** Open the review queue on one document's passages. */
  onReview: (documentId: string) => void;
  /** Change a placement: the add form, pre-filled. */
  onChange: (proposalId: string) => void;
  onOpenInterview: (interviewId: string) => void;
}) {
  const { toast } = useToast();
  const eng = useEngagementQuery(engagementId);
  const [sending, setSending] = React.useState<Sending[]>([]);
  const [over, setOver] = React.useState(false);
  const [showPlaced, setShowPlaced] = React.useState(false);
  const input = React.useRef<HTMLInputElement>(null);
  const proposals = useInterviewProposalsQuery(engagementId);
  const intake = useIntakeQuery(engagementId, { poll: sending.length > 0 });
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });

  const confirmOne = useDiscoveryWrite(engagementId, (id: string) =>
    confirmInterviewProposal(engagementId, id),
  );
  const confirmSure = useDiscoveryWrite(engagementId, (ids: string[]) =>
    confirmInterviewProposals(engagementId, ids),
  );
  const notTranscript = useDiscoveryWrite(engagementId, (id: string) =>
    declineInterviewProposal(engagementId, id, { reason: 'not_an_interview' }),
  );

  const upload = async (files: File[]) => {
    const projectId = eng.data?.project_id;
    if (!projectId) {
      toast({ message: 'The engagement is still loading. Try again in a moment.' });
      return;
    }
    setSending(files.map((f) => ({ name: f.name, pct: 0 })));
    let done = 0;
    for (const file of files) {
      try {
        await uploadFileChunked(transcriptFile(file), {
          projectId,
          title: file.name,
          // Transcripts and the client's own documents (E19 §1), purpose
          // `general` (the default); the classifier decides which is which.
          sourceParty: 'client',
          onProgress: ({ sentBytes, totalBytes }) =>
            setSending((cur) =>
              cur.map((s) =>
                s.name === file.name
                  ? { ...s, pct: totalBytes ? Math.round((sentBytes / totalBytes) * 100) : 100 }
                  : s,
              ),
            ),
        });
        done++;
      } catch (e) {
        fail(e);
      }
      setSending((cur) => cur.filter((s) => s.name !== file.name));
    }
    void intake.refetch();
    if (done > 0) {
      toast({
        message: `${plural(done, 'file')} uploaded. Caliber is sorting ${done === 1 ? 'it' : 'them'}: transcripts get a place to confirm, client documents are read for evidence.`,
      });
    }
  };

  const toPlace = proposals.data?.items ?? [];
  const sure = toPlace.filter((p) => p.preselected && !p.document.stale).map((p) => p.proposal_id);
  const files = intake.data?.files ?? [];
  const read = files.filter((f) => f.state === 'read' || f.state === 'reading');
  const sorting = files.filter((f) => f.state === 'sorting');
  const skipped = files.filter((f) => f.state === 'skipped' || f.state === 'failed');
  const placed = files.filter((f) => f.state === 'placed');

  return (
    <div>
      <DetailHead
        leading={<Upload className="h-[18px] w-[18px] flex-none text-ink-500" aria-hidden="true" />}
        title="Bring in files"
        meta="Transcripts and client documents, many at once. Caliber sorts them; you confirm where each transcript goes."
      />
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label="Files to bring in"
        accept={ACCEPT}
        onChange={(e) => {
          const list = [...(e.target.files ?? [])];
          e.target.value = '';
          if (list.length) void upload(list);
        }}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const list = [...e.dataTransfer.files];
          if (list.length) void upload(list);
        }}
        className={cn(
          'mb-4 flex items-center gap-3.5 rounded-card border-[1.5px] border-dashed px-[18px] py-[22px] text-ink-500',
          over ? 'border-accent-amber-600 bg-accent-amber-50' : 'border-ink-200',
        )}
      >
        <Upload className="h-[18px] w-[18px] flex-none" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-900">Drop files here</div>
          <div className="text-meta">
            Word, PDF, text, captions (.vtt) and spreadsheets. Contracts and vendor documents are
            not read by Discovery.
          </div>
        </div>
        <WorkspaceButton
          variant="primary"
          disabled={sending.length > 0}
          onClick={() => input.current?.click()}
        >
          Choose files
        </WorkspaceButton>
      </div>

      {sending.length > 0 || sorting.length > 0 ? (
        <>
          <PartLabel>Uploading and sorting</PartLabel>
          <Box className="mb-4">
            {sending.map((s) => (
              <FileRow
                key={`up-${s.name}`}
                icon={<FileText className="h-4 w-4" aria-hidden="true" />}
                name={s.name}
                what={
                  <>
                    <div
                      role="progressbar"
                      aria-label={`Uploading ${s.name}`}
                      aria-valuenow={s.pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      className="mt-1.5 h-1.5 max-w-[240px] overflow-hidden rounded-full bg-ink-100"
                    >
                      <i
                        className="block h-full rounded-full bg-accent-amber-600"
                        style={{ width: `${s.pct}%` }}
                      />
                    </div>
                    <span className="text-meta text-ink-500">Uploading {s.pct}%</span>
                  </>
                }
              />
            ))}
            {sorting.map((f) => (
              <FileRow
                key={f.document_id}
                icon={<FileText className="h-4 w-4" aria-hidden="true" />}
                name={f.title}
                what={
                  <span className="text-meta text-ink-500">
                    Sorting: Caliber is checking whether it is a transcript or a client document.
                  </span>
                }
              />
            ))}
          </Box>
        </>
      ) : null}

      <div className="mb-1.5 flex items-center gap-2">
        <PartLabel className="mb-0">Transcripts to place</PartLabel>
        <span className="font-mono text-meta text-ink-500">{toPlace.length}</span>
        <span className="flex-1" />
        {sure.length > 0 ? (
          <button
            type="button"
            className={linkishClass()}
            disabled={confirmSure.isPending}
            onClick={() =>
              confirmSure.mutate(sure, {
                onSuccess: (r) =>
                  toast({
                    message: bulkOutcomeText(r),
                    tone: r.confirmed > 0 ? undefined : 'critical',
                  }),
                onError: fail,
              })
            }
          >
            Confirm the sure ones ({sure.length})
          </button>
        ) : null}
      </div>
      <Box className="mb-4">
        {proposals.error ? <ErrorMessage error={proposals.error} /> : null}
        {toPlace.length === 0 ? (
          <div className="px-3.5 py-3 text-meta text-ink-500">Nothing to place.</div>
        ) : null}
        {toPlace.map((p) => (
          <FileRow
            key={p.proposal_id}
            icon={<Mic className="h-4 w-4" aria-hidden="true" />}
            name={p.document.title}
            what={
              <>
                <div className="text-body-sm text-ink-700">{goesTo(p, interviews)}</div>
                <div className="text-meta text-ink-500">{p.match_text}</div>
              </>
            }
            actions={
              <>
                <StatusChip tone={p.preselected ? 'healthy' : 'amber'} size="sm">
                  {p.document.stale ? 'Newer version' : p.preselected ? 'Sure' : 'Check'}
                </StatusChip>
                <button
                  type="button"
                  className={linkishClass()}
                  disabled={notTranscript.isPending}
                  onClick={() =>
                    notTranscript.mutate(p.proposal_id, {
                      onSuccess: () => toast({ message: `${p.document.title} stays a document.` }),
                      onError: fail,
                    })
                  }
                >
                  Not a transcript
                </button>
                <WorkspaceButton onClick={() => onChange(p.proposal_id)}>Change</WorkspaceButton>
                <WorkspaceButton
                  variant="primary"
                  disabled={confirmOne.isPending || p.document.stale}
                  onClick={() =>
                    confirmOne.mutate(p.proposal_id, {
                      onSuccess: (r) =>
                        toast({
                          message: `${p.document.title} is placed with ${r.interview.code} ${r.interview.title}: the reading starts now; next you say who was in the room.`,
                        }),
                      onError: fail,
                    })
                  }
                >
                  Confirm
                </WorkspaceButton>
              </>
            }
          />
        ))}
      </Box>

      <PartLabel>Read for evidence</PartLabel>
      <Box className="mb-4">
        {intake.isLoading ? (
          <div className="flex items-center gap-2 px-3.5 py-3 text-meta text-ink-500">
            <Spinner /> Loading…
          </div>
        ) : null}
        {intake.error ? <ErrorMessage error={intake.error} /> : null}
        {intake.data && read.length === 0 ? (
          <div className="px-3.5 py-3 text-meta text-ink-500">No client document read yet.</div>
        ) : null}
        {read.map((f) => (
          <FileRow
            key={f.document_id}
            icon={docIcon(f)}
            name={f.title}
            what={
              <div className="text-body-sm text-ink-700">
                {f.state === 'reading'
                  ? 'Client document: Caliber is reading it.'
                  : `Client document, read for evidence: ${
                      f.proposed > 0
                        ? `${plural(f.proposed, 'passage')} to review`
                        : f.confirmed > 0
                          ? `every passage decided, ${f.confirmed} confirmed`
                          : 'nothing proposed'
                    }.`}
              </div>
            }
            actions={
              f.proposed > 0 ? (
                <WorkspaceButton onClick={() => onReview(f.document_id)}>
                  Review {f.proposed}
                </WorkspaceButton>
              ) : f.state === 'read' ? (
                <StatusChip tone="healthy" size="sm">
                  Done
                </StatusChip>
              ) : null
            }
          />
        ))}
      </Box>

      <PartLabel>Nothing to do</PartLabel>
      <Box className="mb-4">
        {intake.data && skipped.length === 0 ? (
          <div className="px-3.5 py-3 text-meta text-ink-500">Nothing skipped.</div>
        ) : null}
        {skipped.map((f) => (
          <FileRow
            key={f.document_id}
            icon={docIcon(f)}
            name={f.title}
            muted
            what={<div className="text-meta text-ink-500">{reasonText(f)}</div>}
          />
        ))}
      </Box>

      {placed.length > 0 ? (
        <>
          <ListGroupLabel
            count={placed.length}
            action={
              <button
                type="button"
                className={linkishClass()}
                onClick={() => setShowPlaced((v) => !v)}
              >
                {showPlaced ? 'Hide' : 'Show'}
              </button>
            }
          >
            Placed with interviews
          </ListGroupLabel>
          {showPlaced ? (
            <Box>
              {placed.map((f) => (
                <FileRow
                  key={f.document_id}
                  icon={<Mic className="h-4 w-4" aria-hidden="true" />}
                  name={f.title}
                  what={
                    <div className="text-meta text-ink-500">
                      {f.interview
                        ? `Placed with ${f.interview.person ? `${f.interview.person}, ` : ''}${f.interview.title} (${f.interview.code}).`
                        : 'Placed with an interview.'}
                    </div>
                  }
                  actions={
                    f.interview ? (
                      <button
                        type="button"
                        className={linkishClass()}
                        onClick={() => onOpenInterview(f.interview!.id)}
                      >
                        Open
                      </button>
                    ) : null
                  }
                />
              ))}
            </Box>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** "Goes to Lena Park, Grants manager (S12)" or "Goes to the Assessor interview (S5, planned, no one named yet)". */
export function goesTo(p: InterviewProposal, interviews: ReadonlyArray<InterviewListItem>) {
  if (p.placement === 'attach' && p.interview) {
    const iv = interviews.find((x) => x.id === p.interview!.id);
    const person = iv?.people[0];
    const where =
      person && iv && iv.people.length === 1
        ? `${person.full_name}, ${person.title || iv.title} (${p.interview.code})`
        : iv && iv.people.length > 1
          ? `${iv.title} (${p.interview.code})`
          : `the ${p.interview.title} interview (${p.interview.code}, ${p.interview.status}, no one named yet)`;
    return (
      <>
        Goes to <b className="font-semibold text-ink-900">{where}</b>
        {p.version_kind === 'corrected' ? ', as a corrected version' : ''}
      </>
    );
  }
  return (
    <>
      Goes to <b className="font-semibold text-ink-900">a new interview</b>
      {p.interview_type ? ` (type ${p.interview_type.name})` : ''}
    </>
  );
}

function docIcon(f: IntakeFile) {
  return /\.(xlsx?|csv)$/i.test(f.title) ? (
    <Database className="h-4 w-4" aria-hidden="true" />
  ) : (
    <FileText className="h-4 w-4" aria-hidden="true" />
  );
}

function Box({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-ink-100 bg-paper-0', className)}>{children}</div>
  );
}

function FileRow({
  icon,
  name,
  what,
  actions,
  muted = false,
}: {
  icon: React.ReactNode;
  name: string;
  what: React.ReactNode;
  actions?: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-ink-100 px-3.5 py-3 last:border-b-0">
      <span className="mt-0.5 flex-none text-ink-500">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className={cn('text-body-sm font-medium', muted ? 'text-ink-500' : 'text-ink-900')}>
          {name}
        </div>
        <div className="mt-0.5">{what}</div>
      </div>
      {actions ? (
        <div className="flex flex-none flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
